import { Controller } from "@hotwired/stimulus"

// A column's type and encryption are fixed once the column exists: changing
// either would mean rewriting every value already stored under it, so Table
// refuses it (existing_columns_type_and_encryption_unchanged). Without this
// the form still offered both, and you found out by losing the edit.
//
// Locked rather than disabled, deliberately. A disabled control is left out of
// the submission, and the encrypted toggle ships with a hidden companion
// carrying 0, so disabling the checkbox would submit "not encrypted" for an
// encrypted column and fail the very validation this exists to avoid. These
// stay submittable and stop responding instead.
const LOCKED_SUFFIXES = ["[type]", "[encrypted]"]
const REASON = "Set when the column was created. Changing it would mean rewriting every value already stored."

export default class extends Controller {
  connect() {
    this.lock()
    // Rows are added, removed and restored after load, and a new row is
    // editable until it has been saved and given an id.
    this.observer = new MutationObserver(() => this.lock())
    this.observer.observe(this.element, { childList: true, subtree: true })

    this.element.addEventListener("click", this.swallow, true)
    this.element.addEventListener("keydown", this.swallow, true)
  }

  disconnect() {
    this.observer?.disconnect()
    this.element.removeEventListener("click", this.swallow, true)
    this.element.removeEventListener("keydown", this.swallow, true)
  }

  // Capture phase, so the click never reaches the control or its label.
  swallow = (event) => {
    if (!event.target.closest?.("[data-locked-column]")) return
    event.preventDefault()
    event.stopPropagation()
  }

  lock() {
    this.element
      .querySelectorAll('[data-controller~="structured-input-row"]')
      .forEach((row) => {
        const id = row.querySelector('input[name$="[id]"]')
        const existing = Boolean(id && id.value.trim())
        row.querySelectorAll("select, input[type=checkbox]").forEach((control) => {
          const name = control.name || ""
          const locked = existing && LOCKED_SUFFIXES.some((suffix) => name.endsWith(suffix))
          this.mark(control, locked)
        })
      })
  }

  mark(control, locked) {
    // The label is part of the toggle, so the whole wrapper has to stop
    // responding, not just the input inside it.
    const wrapper = control.closest(".inner-wrapper") || control

    if (locked) {
      if (wrapper.hasAttribute("data-locked-column")) return
      wrapper.setAttribute("data-locked-column", "")
      wrapper.classList.add("opacity-60", "cursor-not-allowed")
      wrapper.title = REASON
      control.setAttribute("aria-disabled", "true")
      control.tabIndex = -1
    } else if (wrapper.hasAttribute("data-locked-column")) {
      wrapper.removeAttribute("data-locked-column")
      wrapper.classList.remove("opacity-60", "cursor-not-allowed")
      wrapper.removeAttribute("title")
      control.removeAttribute("aria-disabled")
      control.removeAttribute("tabindex")
    }
  }
}
